import React from 'react'
import clsx from 'clsx'
import { ZinIcon } from '@tnl/zin-react'
import { getPasswordChecks, PasswordPolicy } from '@utils/password'
import './PasswordRequirements.scss'

export interface PasswordRequirementsLabels {
  title?: string
  minLength?: string
  lowercase?: string
  uppercase?: string
  number?: string
  symbol?: string
}

export interface PasswordRequirementsProps {
  password: string
  minLength?: number
  policy?: PasswordPolicy
  className?: string
  labels?: PasswordRequirementsLabels
}

export const DEFAULT_PASSWORD_LABELS: Required<PasswordRequirementsLabels> = {
  title: 'Your new password must contain:',
  minLength: 'At least 8 characters',
  lowercase: 'At least 1 lowercase letter (a-z)',
  uppercase: 'At least 1 uppercase letter (A-Z)',
  number: 'At least 1 number (0-9)',
  symbol: 'At least 1 special character (!@#$%^&*)',
}

/**
 * Helper function to pluralize words based on count
 */
const pluralize = (count: number, singular: string): string => (count > 1 ? `${singular}s` : singular)

export const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({ password, minLength = 8, policy, className, labels }) => {
  const value = String(password ?? '')
  const checks = getPasswordChecks(value, policy)
  const activated = value.length >= 4

  // Use policy values or fallback to defaults
  const effectiveMinLength = policy?.minLength || minLength
  const effectiveMinLowerCase = policy?.minLowerCase || 1
  const effectiveMinUpperCase = policy?.minUpperCase || 1
  const effectiveMinNumber = policy?.minNumber || 1
  const effectiveMinSymbol = policy?.minSymbol || 0

  const text = {
    title: labels?.title ?? DEFAULT_PASSWORD_LABELS.title,
    minLength: labels?.minLength ?? `At least ${effectiveMinLength} characters`,
    lowercase: labels?.lowercase ?? `At least ${effectiveMinLowerCase} lowercase ${pluralize(effectiveMinLowerCase, 'letter')} (a-z)`,
    uppercase: labels?.uppercase ?? `At least ${effectiveMinUpperCase} uppercase ${pluralize(effectiveMinUpperCase, 'letter')} (A-Z)`,
    number: labels?.number ?? `At least ${effectiveMinNumber} ${pluralize(effectiveMinNumber, 'number')} (0-9)`,
    symbol: labels?.symbol ?? `At least ${effectiveMinSymbol} special ${pluralize(effectiveMinSymbol, 'character')} (!@#$%^&*)`,
  }

  const renderRequirementItem = (key: keyof typeof checks, label: string) => {
    const isValid = checks[key]
    return (
      <div
        key={key}
        className={clsx(
          'mk-password-requirements__item flex gap-3 items-center',
          activated && 'mk-password-requirements__item--active',
          activated && isValid && 'mk-password-requirements__item--valid',
        )}>
        {activated ? (
          <ZinIcon name={isValid ? 'check' : 'x'} />
        ) : (
          <div className="w-[16px] h-[16px] flex items-center justify-center">
            <ZinIcon
              name="circle"
              size="tiny"
              className="mk-username-requirements__bullet w-[8px] h-[8px]"
            />
          </div>
        )}
        <span>{label}</span>
      </div>
    )
  }

  return (
    <div className={clsx('mk-password-requirements', className)}>
      <div className="mk-password-requirements__title body-2 font-semibold mb-2">{text.title}</div>
      <div className="mk-password-requirements__list flex flex-col gap-1">
        {renderRequirementItem('hasMinLength', text.minLength)}
        {renderRequirementItem('hasLowercase', text.lowercase)}
        {renderRequirementItem('hasUppercase', text.uppercase)}
        {renderRequirementItem('hasNumber', text.number)}
        {effectiveMinSymbol > 0 && renderRequirementItem('hasSymbol', text.symbol)}
      </div>
    </div>
  )
}

export default PasswordRequirements
